
import React from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { DateRangeType } from '../hooks/useScheduleAlerts';
import { useLanguage } from '@/contexts/LanguageContext';

interface DateRangeSelectorProps {
  dateRangeType: DateRangeType;
  customDateRange: [Date | undefined, Date | undefined];
  startDate: Date;
  endDate: Date;
  onSelectDateRangeType: (type: DateRangeType) => void;
  onSelectCustomDateRange: (range: [Date | undefined, Date | undefined]) => void;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ 
  dateRangeType,
  customDateRange,
  startDate,
  endDate,
  onSelectDateRangeType,
  onSelectCustomDateRange
}) => {
  const { t } = useLanguage();
  
  return (
    <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
      <div className="flex items-center space-x-2">
        <span className="text-sm font-medium">{t('dateRange') || 'Date Range'}:</span>
        <Select value={dateRangeType} onValueChange={(value) => onSelectDateRangeType(value as DateRangeType)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder={t('selectRange') || 'Select Range'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">{t('currentWeek') || 'Current Week'}</SelectItem>
            <SelectItem value="twoWeeks">{t('twoWeeks') || 'Two Weeks'}</SelectItem>
            <SelectItem value="month">{t('month') || 'Month'}</SelectItem>
            <SelectItem value="custom">{t('custom') || 'Custom'}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {dateRangeType === 'custom' && (
        <DateRangePicker
          initialDateFrom={customDateRange[0]}
          initialDateTo={customDateRange[1]}
          onUpdate={(range) => {
            if (range.from && range.to) {
              onSelectCustomDateRange([range.from, range.to]);
            }
          }}
        >
          <Button variant="outline" className="flex items-center gap-1 px-3" size="sm">
            <Calendar className="h-4 w-4" />
            <span>
              {customDateRange[0] && customDateRange[1] 
                ? `${format(customDateRange[0], 'MMM d')} - ${format(customDateRange[1], 'MMM d, yyyy')}`
                : `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`}
            </span>
          </Button>
        </DateRangePicker>
      )}
      
      {dateRangeType !== 'custom' && (
        <div className="text-sm text-muted-foreground">
          <span>{format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}</span>
        </div>
      )}
    </div>
  );
};
